import {
    getTopicsWithClausesForChecklist,
    IClauseChecklist,
    IClauseChecklistTopicClausesUpdate,
    IEnabledClauseSection,
    ITopicWithEnabledClauses,
    updateEnabledClausesForTopics,
} from "@/api";
import { LoadingIndicator } from "@/components/shared/LoadingIndicator";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { ClauseChecklistTopicsToolbar } from "./ClauseChecklistTopicsToolbar";
import { TopicAccordion } from "./TopicAccordion";

/**
 * Form data structure for RHF.
 * Flat record: compound key -> enabled boolean
 * Example: { "123:456": true, "123:null": false, "789:456": true }
 */
interface ClauseChecklistFormData {
    clauses: Record<string, boolean>;
}

/**
 * Utility: Convert server data to form default values.
 * Topics with empty enabled_clauses are "all enabled" - we'll populate when expanded.
 */
const buildDefaultValues = (
    topics: ITopicWithEnabledClauses[]
): ClauseChecklistFormData => {
    const clauses: Record<string, boolean> = {};

    topics.forEach((topic) => {
        // When enabled_clauses has items, those specific clauses are enabled
        // Empty array means "all enabled" - handled per-topic when expanded
        if (topic.enabled_clauses.length > 0) {
            topic.enabled_clauses.forEach((ec) => {
                const key = createClauseSectionKey(
                    ec.topic_clause_id,
                    ec.document_section_type_id
                );
                clauses[key] = true;
            });
        }
    });

    return { clauses };
};

interface IProps {
    /**
     * The clause checklist to display the topics for.
     */
    clauseChecklist: IClauseChecklist;
}

/**
 * Create a compound key for a clause-section combination.
 * Format: "topicClauseId:sectionId" (sectionId is "null" for general section)
 */
const createClauseSectionKey = (
    topicClauseId: number,
    documentSectionTypeId: number | null
): string => {
    return `${topicClauseId}:${documentSectionTypeId ?? "null"}`;
};

/**
 * Parse a compound key back to IEnabledClauseSection format (snake_case for API).
 */
const parseClauseSectionKey = (key: string): IEnabledClauseSection => {
    const [topicClauseIdStr, sectionIdStr] = key.split(":");
    return {
        topic_clause_id: parseInt(topicClauseIdStr, 10),
        document_section_type_id:
            sectionIdStr === "null" ? null : parseInt(sectionIdStr, 10),
    };
};

/**
 * Displays topics assigned to a clause checklist with expandable clause checkboxes.
 * Uses React Hook Form for state management with batch saves.
 */
export const ClauseChecklistTopics = ({ clauseChecklist }: IProps) => {
    const queryClient = useQueryClient();

    // Fetch topics with enabled clause information
    const {
        data: topics = [],
        isFetching,
        error,
    } = useQuery({
        queryKey: [
            "admin-clause-checklist-topics-with-clauses",
            clauseChecklist.id,
        ],
        queryFn: () => getTopicsWithClausesForChecklist(clauseChecklist.id),
    });

    // Initialize RHF form
    const { setValue, reset, handleSubmit, formState, control } =
        useForm<ClauseChecklistFormData>({
            defaultValues: { clauses: {} },
        });

    const { isDirty } = formState;

    // useWatch subscribes to form changes and triggers re-renders
    // This is more reliable than watch() for nested object updates
    const clauseValues = useWatch({
        control,
        name: "clauses",
        defaultValue: {},
    });

    // Track which accordion is expanded
    const [expandedTopicId, setExpandedTopicId] = useState<number | null>(null);

    // Track all clause keys per topic (populated when topics expand)
    const [topicClauseKeys, setTopicClauseKeys] = useState<
        Map<number, string[]>
    >(new Map());

    // Track original "all enabled" state per topic (null in server = all enabled)
    const [originalAllEnabled, setOriginalAllEnabled] = useState<
        Map<number, boolean>
    >(new Map());

    // Track original enabled keys per topic for save comparison
    const [originalEnabledKeys, setOriginalEnabledKeys] = useState<
        Map<number, Set<string>>
    >(new Map());

    // Reset form when topics load
    useEffect(() => {
        if (topics.length > 0) {
            const defaultValues = buildDefaultValues(topics);
            reset(defaultValues);

            // Track which topics started as "all enabled"
            const allEnabledMap = new Map<number, boolean>();
            const enabledKeysMap = new Map<number, Set<string>>();

            topics.forEach((topic) => {
                const isAllEnabled = topic.enabled_clauses.length === 0;
                allEnabledMap.set(topic.id, isAllEnabled);

                // Store original enabled keys for topics that have specific clauses
                if (!isAllEnabled) {
                    const keys = topic.enabled_clauses.map((ec) =>
                        createClauseSectionKey(
                            ec.topic_clause_id,
                            ec.document_section_type_id
                        )
                    );
                    enabledKeysMap.set(topic.id, new Set(keys));
                }
            });

            setOriginalAllEnabled(allEnabledMap);
            setOriginalEnabledKeys(enabledKeysMap);

            // Clear cached clause keys so they can be repopulated for the new topics data
            setTopicClauseKeys(new Map());
        }
    }, [topics, reset]);

    // Handler: When topic details load, populate form with clause keys
    // For "all enabled" topics, we need to set all keys to true in the form
    const handleTopicDetailsLoaded = useCallback(
        (topicId: number, allKeys: string[]) => {
            // Store keys for this topic
            setTopicClauseKeys((prev) => new Map(prev).set(topicId, allKeys));

            // If originally "all enabled", set all to true in form (without marking dirty)
            if (originalAllEnabled.get(topicId)) {
                allKeys.forEach((key) => {
                    setValue(`clauses.${key}`, true, { shouldDirty: false });
                });
            }
        },
        [originalAllEnabled, setValue]
    );

    // Toggle single clause
    const handleClauseToggle = useCallback(
        (key: string, enabled: boolean) => {
            setValue(`clauses.${key}`, enabled, { shouldDirty: true });
        },
        [setValue]
    );

    // Select all clauses in a topic
    const handleSelectAllTopic = useCallback(
        (topicId: number) => {
            const keys = topicClauseKeys.get(topicId) ?? [];
            keys.forEach((key) => {
                setValue(`clauses.${key}`, true, { shouldDirty: true });
            });
        },
        [topicClauseKeys, setValue]
    );

    // Deselect all clauses in a topic
    const handleDeselectAllTopic = useCallback(
        (topicId: number) => {
            const keys = topicClauseKeys.get(topicId) ?? [];
            keys.forEach((key) => {
                setValue(`clauses.${key}`, false, { shouldDirty: true });
            });
        },
        [topicClauseKeys, setValue]
    );

    // Select all clauses in a section
    const handleSelectSection = useCallback(
        (sectionKeys: string[]) => {
            sectionKeys.forEach((key) => {
                setValue(`clauses.${key}`, true, { shouldDirty: true });
            });
        },
        [setValue]
    );

    // Deselect all clauses in a section
    const handleDeselectSection = useCallback(
        (sectionKeys: string[]) => {
            sectionKeys.forEach((key) => {
                setValue(`clauses.${key}`, false, { shouldDirty: true });
            });
        },
        [setValue]
    );

    // Save mutation using form data
    const saveMutation = useMutation({
        mutationFn: async (formData: ClauseChecklistFormData) => {
            // Build update payload - only include topics that have changed
            const updates: IClauseChecklistTopicClausesUpdate["updates"] = [];

            for (const [topicId, allKeys] of topicClauseKeys) {
                // Get currently enabled keys for this topic from form
                const enabledKeys = allKeys.filter(
                    (key) => formData.clauses[key]
                );

                // Check if changed from original
                const wasAllEnabled = originalAllEnabled.get(topicId);
                const originalKeys = originalEnabledKeys.get(topicId);

                let changed = false;

                if (wasAllEnabled) {
                    // Originally all enabled - changed if not all are enabled now
                    changed = enabledKeys.length !== allKeys.length;
                } else if (originalKeys) {
                    // Compare sets
                    if (enabledKeys.length !== originalKeys.size) {
                        changed = true;
                    } else {
                        for (const key of enabledKeys) {
                            if (!originalKeys.has(key)) {
                                changed = true;
                                break;
                            }
                        }
                    }
                }

                if (changed) {
                    // Convert keys back to IEnabledClauseSection objects
                    const enabledClauses: IEnabledClauseSection[] =
                        enabledKeys.map((key) => parseClauseSectionKey(key));

                    updates.push({
                        topic_id: topicId,
                        enabled_clauses: enabledClauses,
                    });
                }
            }

            if (updates.length > 0) {
                await updateEnabledClausesForTopics(clauseChecklist.id, {
                    updates,
                });
            }
        },
        onSuccess: () => {
            enqueueSnackbar("Changes saved successfully.", {
                variant: "success",
            });

            // Invalidate queries to refresh data
            queryClient.invalidateQueries({
                queryKey: [
                    "admin-clause-checklist-topics-with-clauses",
                    clauseChecklist.id,
                ],
            });
            // Also invalidate the basic topics query used elsewhere
            queryClient.invalidateQueries({
                queryKey: ["admin-clause-checklist-topics", clauseChecklist.id],
            });
        },
        onError: () => {
            enqueueSnackbar("Failed to save changes. Please try again.", {
                variant: "error",
            });
        },
    });

    // Handle save using RHF handleSubmit
    const onSubmit = handleSubmit((data) => {
        saveMutation.mutate(data);
    });

    // Handle accordion toggle
    const handleToggle = useCallback((topicId: number) => {
        setExpandedTopicId((prev) => (prev === topicId ? null : topicId));
    }, []);

    if (error) {
        return (
            <Box p={2}>
                <Alert severity="error">
                    Failed to load topics. Please try again.
                </Alert>
            </Box>
        );
    }

    return (
        <Stack spacing={2} sx={{ py: 2 }}>
            {/* Toolbar */}
            <ClauseChecklistTopicsToolbar
                clauseChecklist={clauseChecklist}
                hasChanges={isDirty}
                isSaving={saveMutation.isPending}
                onSave={onSubmit}
            />

            {/* Loading state */}
            {isFetching && topics.length === 0 && <LoadingIndicator />}

            {/* Empty state */}
            {!isFetching && topics.length === 0 && (
                <Box
                    sx={{
                        p: 4,
                        textAlign: "center",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.paper",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        No topics have been added to this checklist yet.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Click "Add Topics" to get started.
                    </Typography>
                </Box>
            )}

            {/* Topic accordions */}
            {topics.length > 0 && (
                <Stack>
                    {topics.map((topic) => (
                        <TopicAccordion
                            key={topic.id}
                            topic={topic}
                            clauseChecklistId={clauseChecklist.id}
                            expanded={expandedTopicId === topic.id}
                            onToggle={() => handleToggle(topic.id)}
                            clauseValues={clauseValues}
                            onTopicDetailsLoaded={handleTopicDetailsLoaded}
                            onClauseToggle={handleClauseToggle}
                            onSelectAllTopic={handleSelectAllTopic}
                            onDeselectAllTopic={handleDeselectAllTopic}
                            onSelectSection={handleSelectSection}
                            onDeselectSection={handleDeselectSection}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    );
};
